import clsx from 'clsx';
import React, { ReactElement } from 'react';
import i18next from 'i18n';
import DSButton from '@/components/design-system/Button';
import Icon, { IconName } from '@/components/design-system/Icon';

/**
 * Empty state — master 272:2529 (Empty States - Profile), which draws four
 * variants that differ only in their words: a 56px surface-100 tile holding a
 * 20px glyph, the headline in Header/H2, a 320-wide line of Body/300 under it,
 * and a 300-wide secondary action with the arrow on its right. 16px between
 * each, 64 above and below.
 *
 * One component covers every empty surface in the app: the buying and account
 * tabs, the cart, alerts, checkout, the PDP bid and offer histories and
 * search. So it is tokenised here rather than per call site, and the profile's
 * four variants are just copy passed in.
 *
 * Was a 48px stroked glyph on surface-400 with no tile, an H3 headline and a
 * Body/400 subtitle, above a PRIMARY button — the master's is secondary, which
 * matters here: a dark button is the page's main action, and "there is nothing
 * here yet" should not outrank the real actions around it.
 *
 * The subtitle colour is a literal because the master's secondary text is
 * #595958 while our --ds-ink-200 is #A3A298; that token is flagged as an open
 * question in globals.scss and correcting it would recolour 38 other places.
 *
 * Deliberately not a hook-using component: several callers invoke it as a
 * plain function — `EmptyState({ title, subtitle })` — rather than as JSX.
 */
interface EmptyStateProps {
    title?: string;
    /** Takes a node, not just a string: the search variant puts a link in it. */
    subtitle?: React.ReactNode;
    /** Glyph in the tile. The comps vary it by what is empty. */
    icon?: IconName;
    customIcon?: ReactElement;
    hasIcon?: boolean;
    onClick?: () => void;
    onLinkClick?: () => void;
    actionIcon?: ReactElement;
    actionText?: string;
    pathRoute?: string;
    /** Opens the action in a new tab — used when it leaves for support. */
    actionExternal?: boolean;
    noPadding?: boolean
}

export default function EmptyState({ title, subtitle, icon = 'bag', customIcon, hasIcon = true, onLinkClick, onClick, actionIcon, actionText, pathRoute, actionExternal = false, noPadding = false }: EmptyStateProps) {
    const action = actionText || (onClick ? i18next.t('button-text.add') : i18next.t('button-text.view'));

    return (
        <div className={clsx('flex flex-1 items-center justify-center', noPadding ? '' : 'py-[64px]')}>
            <div className="flex flex-col items-center justify-center gap-[16px] text-center normal-case">
                {customIcon || (
                    hasIcon ? (
                        <div className="flex size-[56px] shrink-0 items-center justify-center bg-surface-100 text-ink-400">
                            <Icon name={icon} size={20} />
                        </div>
                    ) : null
                )}
                {title ? <h3 className="text-h2 text-ink-400">{title}</h3> : null}
                {subtitle ? <p className="text-body-300 w-[320px] max-w-full text-[#595958]">{subtitle}</p> : null}

                {(onClick || pathRoute) && (
                    <div className="w-[300px] max-w-full">
                        <DSButton
                            variant="secondary"
                            size="mobile"
                            fullWidth
                            // DS Button forwards onClick to its Link too, so
                            // the route case still gets its onLinkClick.
                            onClick={onClick || onLinkClick}
                            href={onClick ? undefined : pathRoute}
                            linkTarget={actionExternal ? '_blank' : undefined}
                            icon={actionIcon || <Icon name={onClick ? 'plus' : 'arrow-right'} size={20} />}
                        >
                            {action}
                        </DSButton>
                    </div>
                )}
            </div>
        </div>
    );
}
